import type { EditorHost } from '@blocksuite/block-std';
import { WithDisposable } from '@blocksuite/block-std';
import type { Doc } from '@blocksuite/store';
import { AIProvider } from '@pique/core/blocksuite/presets/ai';
import { AIDoneIcon, AIStarIcon, CloseIcon, RetryIcon } from '@pique/core/blocksuite/presets/ai/_common/icons';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import type { PiqueMermaidBlockModel } from './mermaid-block';

type EditorMode = 'code' | 'describe';

@customElement('mermaid-editor-menu')
export class MermaidEditorMenu extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: block;
    }

    .mermaid-editor-container {
      display: flex;
      flex-direction: column;
      padding: 12px;
      border-radius: 8px;
      border: 1px solid var(--affine-border-color);
      background: var(--affine-background-overlay-panel-color);
      box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.16);
      width: 360px;
      max-width: calc(100% - 24px);
      gap: 10px;
    }

    .mermaid-editor-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .mermaid-mode-toggle {
      display: flex;
      background: var(--affine-background-tertiary-color);
      border-radius: 6px;
      padding: 2px;
      gap: 2px;
    }

    .mermaid-mode-btn {
      padding: 4px 10px;
      font-size: 12px;
      font-weight: 500;
      border: none;
      background: transparent;
      color: var(--affine-text-secondary-color);
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.15s ease;
    }

    .mermaid-mode-btn.active {
      background: var(--affine-background-overlay-panel-color);
      color: var(--affine-text-primary-color);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    .mermaid-mode-btn:hover:not(.active) {
      color: var(--affine-text-primary-color);
    }

    .mermaid-close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 4px;
      cursor: pointer;
      color: var(--affine-icon-color);
      background: transparent;
      border: none;
    }

    .mermaid-close-btn:hover {
      background: var(--affine-hover-color);
    }

    .mermaid-close-btn svg {
      width: 16px;
      height: 16px;
    }

    .mermaid-input-wrapper {
      position: relative;
    }

    .mermaid-editor-input {
      width: 100%;
      height: 140px;
      padding: 10px 12px;
      padding-bottom: 44px;
      border-radius: 6px;
      background: var(--affine-white-10);
      border: 1px solid var(--affine-border-color);
      font-family: var(--affine-font-code-family);
      font-size: 12px;
      line-height: 1.5;
      color: var(--affine-text-primary-color);
      resize: none;
      box-sizing: border-box;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .mermaid-editor-input.describe-mode {
      font-family: var(--affine-font-family);
      font-size: 13px;
    }

    .mermaid-editor-input:focus {
      outline: none;
      border-color: var(--affine-primary-color);
      box-shadow: 0px 0px 0px 2px rgba(30, 150, 235, 0.3);
    }

    .mermaid-editor-input::placeholder {
      color: var(--affine-placeholder-color);
      font-family: var(--affine-font-family);
      font-size: 13px;
    }

    .mermaid-editor-input:disabled {
      opacity: 0.7;
    }

    .mermaid-input-actions {
      position: absolute;
      bottom: 8px;
      right: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .mermaid-icon-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 18px;
      cursor: pointer;
      border: 1px solid var(--affine-border-color);
      background: var(--affine-background-overlay-panel-color);
      transition: all 0.15s ease;
    }

    .mermaid-icon-btn.confirm {
      border-color: var(--affine-primary-color);
      background: var(--affine-primary-color);
      color: white;
    }

    .mermaid-icon-btn.confirm:hover {
      opacity: 0.9;
    }

    .mermaid-icon-btn.redo {
      color: var(--affine-text-primary-color);
    }

    .mermaid-icon-btn.redo:hover {
      background: var(--affine-hover-color);
    }

    .mermaid-icon-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .mermaid-icon-btn svg {
      width: 16px;
      height: 16px;
    }

    .mermaid-generate-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      border: 1px solid var(--affine-border-color);
      background: var(--affine-background-overlay-panel-color);
      color: var(--affine-text-primary-color);
      transition: all 0.15s ease;
    }

    .mermaid-generate-btn:hover:not(:disabled) {
      background: var(--affine-hover-color);
      border-color: var(--affine-primary-color);
    }

    .mermaid-generate-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .mermaid-generate-btn svg {
      width: 16px;
      height: 16px;
    }

    .mermaid-loading-indicator {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      background: var(--affine-background-overlay-panel-color);
      border: 1px solid var(--affine-border-color);
      border-radius: 8px;
      font-size: 12px;
      color: var(--affine-text-secondary-color);
    }

    .mermaid-loading-spinner {
      width: 12px;
      height: 12px;
      border: 2px solid var(--affine-border-color);
      border-top-color: var(--affine-primary-color);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .mermaid-error-message {
      padding: 8px 12px;
      background: var(--affine-background-error-color);
      border-radius: 6px;
      font-size: 12px;
      color: var(--affine-error-color);
    }
  `;

  @property({ attribute: false })
  accessor model!: PiqueMermaidBlockModel;

  @property({ attribute: false })
  accessor doc!: Doc;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor abortController!: AbortController;

  @query('textarea')
  private accessor _textarea!: HTMLTextAreaElement;

  @state()
  private accessor _codeValue = '';

  @state()
  private accessor _describeValue = '';

  @state()
  private accessor _mode: EditorMode = 'code';

  @state()
  private accessor _isGenerating = false;

  @state()
  private accessor _error: string | null = null;

  @state()
  private accessor _hasGenerated = false;

  private _aiAbortController: AbortController | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this._codeValue = this.model.code || '';

    this.disposables.addFromEvent(this, 'keydown', (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelGeneration();
        this.abortController.abort();
      }
    });

    this.disposables.addFromEvent(this, 'pointerdown', (e: PointerEvent) => {
      e.stopPropagation();
    });
    this.disposables.addFromEvent(this, 'pointerup', (e: PointerEvent) => {
      e.stopPropagation();
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._cancelGeneration();
  }

  override firstUpdated() {
    requestAnimationFrame(() => {
      if (this._textarea) {
        this._textarea.focus();
        this._textarea.setSelectionRange(
          this._textarea.value.length,
          this._textarea.value.length
        );
      }
    });
  }

  private _handleCodeInput(e: InputEvent) {
    const value = (e.target as HTMLTextAreaElement).value;
    this._codeValue = value;
    this.doc.updateBlock(this.model, { code: value });
  }

  private _handleDescribeInput(e: InputEvent) {
    const value = (e.target as HTMLTextAreaElement).value;
    this._describeValue = value;
    this._error = null;
  }

  private _setMode(mode: EditorMode) {
    this._mode = mode;
    this._error = null;
    this._hasGenerated = false;
    requestAnimationFrame(() => {
      if (this._textarea) {
        this._textarea.focus();
      }
    });
  }

  private _cancelGeneration() {
    if (this._aiAbortController) {
      this._aiAbortController.abort();
      this._aiAbortController = null;
    }
    this._isGenerating = false;
  }

  private async _generateMermaid() {
    const description = this._describeValue.trim();
    if (!description || this._isGenerating) return;

    this._isGenerating = true;
    this._error = null;
    this._aiAbortController = new AbortController();

    try {
      const stream = AIProvider.actions.createMermaid?.({
        content: description,
        docId: this.doc.id,
        workspaceId: this.doc.collection.id,
        host: this.host,
        stream: true,
        signal: this._aiAbortController.signal,
        where: 'ai-panel',
        control: 'chat-send',
      });

      if (!stream) {
        throw new Error('AI service not available');
      }

      let fullResponse = '';
      for await (const chunk of stream) {
        if (this._aiAbortController?.signal.aborted) break;
        fullResponse += chunk;
      }

      if (this._aiAbortController?.signal.aborted) return;

      const mermaidCode = this._extractMermaidCode(fullResponse);
      this._codeValue = mermaidCode;
      this.doc.updateBlock(this.model, { code: mermaidCode });
      this._hasGenerated = true;
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Mermaid AI generation error:', err);
        this._error = err instanceof Error ? err.message : 'Generation failed';
      }
    } finally {
      this._isGenerating = false;
      this._aiAbortController = null;
    }
  }

  private _extractMermaidCode(response: string): string {
    let code = response.trim();

    const codeBlockMatch = code.match(/```(?:mermaid)?\s*([\s\S]*?)```/);
    if (codeBlockMatch?.[1]) {
      code = codeBlockMatch[1].trim();
    }

    return code;
  }

  private _confirmGeneration() {
    this._hasGenerated = false;
    this._describeValue = '';
    this.abortController.abort();
  }

  private _redoGeneration() {
    this._error = null;
    this._generateMermaid();
  }

  override render() {
    const codePlaceholder = `flowchart TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Action]
    B -->|No| D[End]`;

    const describePlaceholder = `Describe what you want to visualize...`;

    const isDescribeMode = this._mode === 'describe';
    const canGenerate = isDescribeMode && this._describeValue.trim().length > 0 && !this._isGenerating;

    return html`
      <div class="mermaid-editor-container">
        <div class="mermaid-editor-header">
          <div class="mermaid-mode-toggle">
            <button
              class="mermaid-mode-btn ${this._mode === 'code' ? 'active' : ''}"
              @click=${() => this._setMode('code')}
            >
              Code
            </button>
            <button
              class="mermaid-mode-btn ${this._mode === 'describe' ? 'active' : ''}"
              @click=${() => this._setMode('describe')}
            >
              Describe
            </button>
          </div>
          <button class="mermaid-close-btn" @click=${() => this.abortController.abort()}>
            ${CloseIcon}
          </button>
        </div>

        ${this._error
          ? html`<div class="mermaid-error-message">${this._error}</div>`
          : nothing}

        <div class="mermaid-input-wrapper">
          <textarea
            class="mermaid-editor-input ${isDescribeMode ? 'describe-mode' : ''}"
            .value=${isDescribeMode ? this._describeValue : this._codeValue}
            @input=${isDescribeMode ? this._handleDescribeInput : this._handleCodeInput}
            placeholder=${isDescribeMode ? describePlaceholder : codePlaceholder}
            ?disabled=${this._isGenerating}
          ></textarea>

          <div class="mermaid-input-actions">
            ${this._isGenerating
              ? html`
                  <div class="mermaid-loading-indicator">
                    <div class="mermaid-loading-spinner"></div>
                    <span>Generating...</span>
                  </div>
                `
              : nothing}
            ${isDescribeMode && this._hasGenerated && !this._isGenerating
              ? html`
                  <button
                    class="mermaid-icon-btn redo"
                    @click=${this._redoGeneration}
                    title="Regenerate"
                  >
                    ${RetryIcon}
                  </button>
                  <button
                    class="mermaid-icon-btn confirm"
                    @click=${this._confirmGeneration}
                    title="Confirm"
                  >
                    ${AIDoneIcon}
                  </button>
                `
              : nothing}
            ${isDescribeMode && !this._hasGenerated && !this._isGenerating
              ? html`
                  <button
                    class="mermaid-generate-btn"
                    @click=${this._generateMermaid}
                    ?disabled=${!canGenerate}
                  >
                    ${AIStarIcon}
                    Generate
                  </button>
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mermaid-editor-menu': MermaidEditorMenu;
  }
}
